import React, { useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { ChevronRight } from "lucide-react";
import {
  GroupData,
  LanguageNameSpaces,
  MarketplaceSubGroup,
} from "@shared/types";
import {
  marketplaceSelectors,
  selectMarketplaceLoadingStates,
} from "@shared/slices/marketplace.slice";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNetPnlChange } from "@shared/features/newMarketplace/utils";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { typography } from "@shared/common/Themes/typography";
import { AnalyticsContext } from "analytics/context";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import {
  GroupLogo,
  PnlValue,
  TrialLabel,
} from "features/newMarketplace/NewLook/NewLookGroupParts";
import {
  BOT_FORM_RECOMMENDED_GROUP_ID,
  BOT_FORM_RECOMMENDED_GROUP_PNL_ID,
} from "testIds";

const RecommendedMarketplaceGroups = (): React.JSX.Element | null => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { groups, loading } = useRecommendedGroups();
  if (!groups.length && !loading) return null;
  return (
    <Recommendations aria-busy={loading}>
      <Text $weight={600}>{t("no_signals_suggestion.top_groups")}</Text>
      <LoadingSpinnerOverlay isLoading={loading} hideContentWhileLoading={true}>
        <GroupList>
          {groups.map((group, index) => (
            <RecommendedGroup
              key={group.group_id}
              group={group}
              position={index + 1}
            />
          ))}
        </GroupList>
      </LoadingSpinnerOverlay>
    </Recommendations>
  );
};

export default RecommendedMarketplaceGroups;

const useRecommendedGroups = (): {
  groups: MarketplaceSubGroup[];
  loading: boolean;
} => {
  const groupsSelector = useMemo(
    () => marketplaceSelectors.selectSubgroups(false),
    []
  );
  const loadingSelector = useMemo(
    () => selectMarketplaceLoadingStates(false),
    []
  );
  const groups = useSelector(groupsSelector);
  const { loading } = useSelector(loadingSelector);
  const { subGroup, getGroupsDataStatus } = useContext(BotFormContext)!;
  return {
    groups: getAvailableTrialGroups(
      groups,
      getGroupsDataStatus.data,
      subGroup?.id
    ),
    loading: loading || getGroupsDataStatus.isLoading,
  };
};

const getAvailableTrialGroups = (
  groups: MarketplaceSubGroup[],
  followedGroups: GroupData[] | undefined,
  currentGroupId?: number
): MarketplaceSubGroup[] => {
  if (!followedGroups) return [];
  const followedGroupIds = new Set(followedGroups.map((group) => group.id));
  return groups
    .filter(
      (group) =>
        group.has_free_trial &&
        group.group_id !== currentGroupId &&
        !followedGroupIds.has(group.group_id)
    )
    .sort(
      (firstGroup, secondGroup) =>
        secondGroup.backtesting_score - firstGroup.backtesting_score
    )
    .slice(0, 3);
};

const RecommendedGroup = ({
  group,
  position,
}: {
  group: MarketplaceSubGroup;
  position: number;
}): React.JSX.Element => {
  const selectGroup = useSelectGroup(position);
  return (
    <GroupButton
      type="button"
      onClick={() => selectGroup(group)}
      data-testid={BOT_FORM_RECOMMENDED_GROUP_ID}
    >
      <GroupLogo image={group.image_path} name={group.name} size={32} />
      <GroupIdentity group={group} />
      <GroupScore score={group.backtesting_score} />
      <ChevronRight size={16} aria-hidden={true} />
    </GroupButton>
  );
};

const GroupIdentity = ({
  group,
}: {
  group: MarketplaceSubGroup;
}): React.JSX.Element => (
  <Identity>
    <Text $ellipsis={true} $weight={500} title={group.name}>
      {group.name}
    </Text>
    <Metadata>
      <TrialLabel hasFreeTrial={group.has_free_trial} isFree={group.is_free} />
      <GroupPnl group={group} />
    </Metadata>
  </Identity>
);

const GroupPnl = ({
  group,
}: {
  group: MarketplaceSubGroup;
}): React.JSX.Element => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const value = useGroupPnl(group);
  return (
    <PnlMetadata data-testid={BOT_FORM_RECOMMENDED_GROUP_PNL_ID}>
      <Text type="secondary" $size={typography.fontSizes.S}>
        {t("card.net_pnl_1m")}
      </Text>
      <GroupPnlValue value={value} />
    </PnlMetadata>
  );
};

const useGroupPnl = (group: MarketplaceSubGroup): number | undefined => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const initialFunds =
    group.timeframe_to_backtesting_run_data?.[30]?.initial_portfolio_size?.[
      displayCurrency
    ];
  return initialFunds ? getNetPnlChange(group, 30, displayCurrency) : undefined;
};

const GroupPnlValue = ({
  value,
}: {
  value: number | undefined;
}): React.JSX.Element =>
  value === undefined ? (
    <Text type="secondary">—</Text>
  ) : (
    <PnlValue value={value} role="meta" />
  );

const GroupScore = ({ score }: { score: number }): React.JSX.Element => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  return (
    <Score>
      <Text type="secondary" $size={typography.fontSizes.S}>
        {t("score.title")}
      </Text>
      <Text $weight={600}>{Math.round(score * 100)}</Text>
    </Score>
  );
};

const useTrackGroupSelection = (
  position: number
): ((group: MarketplaceSubGroup) => void) => {
  const { analytics } = useContext(AnalyticsContext);
  const { subGroup } = useContext(BotFormContext)!;
  const onboarding = useContext(OnboardingContext);
  return (group) =>
    analytics?.signalsBotMarketplaceGroupSelected({
      marketplaceGroupId: group.group_id,
      marketplaceGroupName: group.name,
      previousGroupId: subGroup?.id,
      recommendationPosition: position,
      formContext: onboarding ? "onboarding" : "bot_form",
      marketplaceGroupOffersFreeTrial: group.has_free_trial,
    });
};

const useSelectGroup = (
  position: number
): ((group: MarketplaceSubGroup) => void) => {
  const trackSelection = useTrackGroupSelection(position);
  const { setSubGroup, setSubGroupBotConfiguration } =
    useContext(BotFormContext)!;
  const onboarding = useContext(OnboardingContext);
  const history = useHistory();
  const location = useLocation();
  return (group) => {
    trackSelection(group);
    setSubGroup({
      id: group.group_id,
      name: group.name,
      image: group.image_path,
      isFreeTrialGroup: !onboarding,
      groupData: group,
    });
    setSubGroupBotConfiguration(undefined);
    if (onboarding) {
      const search = new URLSearchParams(location.search);
      search.set("groupID", group.group_id.toString());
      history.replace({ ...location, search: search.toString() });
    }
  };
};

const Recommendations = styled.div`
  width: 100%;
  margin-top: 4px;
`;
const GroupList = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 8px;
  min-height: 64px;
`;
const GroupButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  width: 100%;
  padding: 12px;
  text-align: start;
  font-family: inherit;
  color: ${(props) => props.theme.colors.secondaryText};
  background: ${(props) => props.theme.colors.cardBgColor};
  border: ${(props) => props.theme.borders.lightest};
  border-radius: ${(props) => props.theme.radii.control}px;
  cursor: pointer;
  &:hover {
    border-color: ${(props) => props.theme.colors.primaryBlue};
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlue};
    outline-offset: 2px;
  }
`;
const Identity = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  > p {
    max-width: 100%;
  }
`;
const Score = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Metadata = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 12px;
  row-gap: 4px;
`;
const PnlMetadata = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
`;
